import React, { useState, useEffect, useMemo } from 'react';
import {
  Layout,
  Typography,
  Space,
  Button,
  Card,
  Row,
  Col,
  Menu,
  Progress,
  Tag,
  Dropdown,
  Avatar,
  Spin,
  Skeleton,
  Empty,
  Tabs,
  Tooltip,
  Modal,
  message,
  Badge,
  Calendar,
  Select,
  Popconfirm,
  Table,
  Grid,
  Flex
} from 'antd';
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CodeOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined,
  TeamOutlined,
  CloudOutlined,
  BookOutlined,
  RightOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  UnorderedListOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  TrophyOutlined,
  StopOutlined,
  LockOutlined,
  LogoutOutlined,
  InfoCircleOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAssignedTrainings, enrollInTraining, requestTrainingAccess, withdrawFromTraining, getTrainingModules, getTrainingLeaderboard } from '../services/trainingService';
import { getMoodlePrograms } from '../services/moodleService';
import { useAuth } from '../hooks/useAuth';
import dayjs, { Dayjs } from 'dayjs';
import { parseModuleId } from '../utils/moduleUtils';
import { Training, TrainingStatus, TrainingType, Module, LeaderboardEntry } from '../types/training';

const { Title, Text, Paragraph } = Typography;
const { Sider, Content } = Layout;

const CALENDAR_COLORS = [
  '#1890ff', // blue
  '#52c41a', // green
  '#faad14', // gold
  '#f5222d', // red
  '#722ed1', // purple
  '#eb2f96', // pink
  '#fa541c', // orange
  '#13c2c2', // cyan
];

const getTrainingPalette = (index: number) => {
  const color = CALENDAR_COLORS[index % CALENDAR_COLORS.length];
  return {
    main: color,
    bg: `${color}15`,
  };
};


// --- Dashboard Component ---
const { useBreakpoint } = Grid;

const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [globalLeaderboard, setGlobalLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState<boolean>(false);
  const [filterKey, setFilterKey] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [activeTab, setActiveTab] = useState<string>('my-learning');
  const [selectedCalendarPrograms, setSelectedCalendarPrograms] = useState<string[]>([]);
  const [selectedCalendarTraining, setSelectedCalendarTraining] = useState<Training | null>(null);
  const [calendarModules, setCalendarModules] = useState<Module[]>([]);
  const [modulesLoading, setModulesLoading] = useState<boolean>(false);

  // Derive the curriculum display list from the assessments JSON, which is the
  // source of truth for which modules belong to the program. Two AI-led structures
  // are supported:
  //   1. Subject → Chapter → Module  (parsed[0].chapters !== undefined)
  //   2. Direct flat modules          (array of module objects without chapters)
  // Trainer-led and programs without JSON assessments fall back to calendarModules.
  const displayModules = useMemo(() => {
    if (!selectedCalendarTraining?.assessments) return calendarModules;
    try {
      const parsed = JSON.parse(selectedCalendarTraining.assessments);
      if (!Array.isArray(parsed) || parsed.length === 0) return calendarModules;

      if (parsed[0].chapters !== undefined) {
        // Type 1: Subject → Chapter → Module hierarchy
        return parsed.flatMap((s: any) =>
          (s.chapters || []).flatMap((c: any) =>
            (c.modules || []).map((m: any, i: number) => ({
              moduleId: m.id || String(m.moodleId || i),
              name: m.name || '',
              description: m.description || '',
              status: 'Not Started' as const,
              questionCount: 0,
              trainingId: selectedCalendarTraining.trainingId,
              assignedDate: ''
            }))
          )
        );
      }

      if (parsed[0].name !== undefined) {
        // Type 2: Direct flat module list
        return parsed.map((m: any, i: number) => ({
          moduleId: m.id || String(m.moodleId || i),
          name: m.name || '',
          description: m.description || '',
          status: 'Not Started' as const,
          questionCount: 0,
          trainingId: selectedCalendarTraining.trainingId,
          assignedDate: ''
        }));
      }
    } catch {}
    return calendarModules;
  }, [selectedCalendarTraining, calendarModules]);

  useEffect(() => {
    if (user?.username) {
      loadTrainings();
    }
  }, [user]);

  useEffect(() => {
    if (selectedCalendarTraining) {
      fetchCalendarModules(selectedCalendarTraining.trainingId);
    } else {
      setCalendarModules([]);
    }
  }, [selectedCalendarTraining]);

  const fetchCalendarModules = async (trainingId: string) => {
    try {
      setModulesLoading(true);
      const modules = await getTrainingModules(trainingId);
      setCalendarModules(modules);
    } catch (error) {
      console.error('Error fetching modules for calendar', error);
    } finally {
      setModulesLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    try {
      setLoadingLeaderboard(true);
      const data = await getTrainingLeaderboard(''); // Empty string for global
      setGlobalLeaderboard(data);
    } catch (error) {
      console.error('Error loading global leaderboard', error);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const loadTrainings = async () => {
    try {
      setLoading(true);
      const data = await getMoodlePrograms(user?.username || "");
      setTrainings(data);
      loadLeaderboard();
    } catch (error) {
      console.error('Error loading trainings', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (trainingId: string) => {
    try {
      await withdrawFromTraining(trainingId);
      message.success('Successfully withdrawn from training');
      loadTrainings(); // Refresh dashboard
    } catch (error) {
      console.error('Error withdrawing', error);
      message.error('Failed to withdraw from training');
    }
  };

  const myTrainings = useMemo(() => {
    // Architectural Fix: The Moodle API now strictly returns "Not Enrolled" if 
    // the user is not a Student. So we can use a very simple filter.
    return trainings.filter(t => (
      t.isMandatory || 
      (t.status !== TrainingStatus.NOT_ENROLLED && t.status !== TrainingStatus.DENIED)
    ));
  }, [trainings]);

  const catalogTrainings = useMemo(() => {
    const myIds = new Set(myTrainings.map(t => t.trainingId));
    return trainings.filter(t => !myIds.has(t.trainingId));
  }, [trainings, myTrainings]);

  const calendarTrainings = useMemo(() => {
    // Shows all programs except evergreen programs (those without dates)
    return trainings
      .filter(t => t.startDate && t.startDate !== '')
      .sort((a, b) => dayjs(a.startDate.split('T')[0]).unix() - dayjs(b.startDate.split('T')[0]).unix());
  }, [trainings]);

  // My Learning's "Upcoming Schedule" panel: only strictly future programs -
  // startDate after today. Running programs (already started, even if today is
  // their start date) and past-ended programs are both excluded entirely.
  // Evergreen programs (no dates) are excluded too, since they have no date to
  // place them on.
  const scheduleTrainings = useMemo(() => {
    const today = dayjs().startOf('day');
    return trainings.filter(t => {
      if (!t.startDate || !t.endDate || t.startDate === '' || t.endDate === '') return false;
      const start = dayjs(t.startDate.split('T')[0]);
      return start.isAfter(today, 'day');
    });
  }, [trainings]);

  // One block per program START date only - a program appears exactly once, on
  // the day it starts (not repeated across its whole duration). Dates with no
  // program starting on them simply never get a block, so "skipping empty dates"
  // falls out for free - there's nothing to skip.
  const scheduleDateBlocks = useMemo(() => {
    const byStartDate = new Map<string, { date: Dayjs; programs: Training[] }>();
    scheduleTrainings.forEach(t => {
      const start = dayjs(t.startDate.split('T')[0]);
      const key = start.format('YYYY-MM-DD');
      const existing = byStartDate.get(key);
      if (existing) {
        existing.programs.push(t);
      } else {
        byStartDate.set(key, { date: start, programs: [t] });
      }
    });
    return Array.from(byStartDate.values()).sort((a, b) => a.date.valueOf() - b.date.valueOf());
  }, [scheduleTrainings]);

  // The panel is only rendered when something is actually upcoming - an empty
  // card would just take a third of the page for no information.
  const hasUpcomingSchedule = scheduleDateBlocks.length > 0;

  const stats = useMemo(() => {
    return {
      all: myTrainings.length,
      notStarted: myTrainings.filter(t => t.status === TrainingStatus.NOT_STARTED).length,
      inProgress: myTrainings.filter(t => t.status === TrainingStatus.IN_PROGRESS).length,
      completed: myTrainings.filter(t => t.status === TrainingStatus.COMPLETED).length,
      incomplete: myTrainings.filter(t => t.status === TrainingStatus.INCOMPLETE || t.status === TrainingStatus.ESCALATED).length,
    };
  }, [myTrainings]);

  const renderTrainingGridItem = (
    item: Training,
    colSpan: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number } = { xs: 24, sm: 12, md: 8, lg: 8, xl: 6 }
  ) => {
    const isCompleted = item.status === TrainingStatus.COMPLETED;
    const isEscalated = item.status === TrainingStatus.ESCALATED;
    const isInProgress = item.status === TrainingStatus.IN_PROGRESS;
    const isNotStarted = item.status === TrainingStatus.NOT_STARTED;
    const progressPercent = item.percentage !== undefined ? item.percentage : (isCompleted ? 100 : 0);
    const isEvergreen = item.trainingType === TrainingType.OPEN && (!item.startDate || item.startDate === '');
    const isDeactivated = !item.active;

    return (
      <Col xs={colSpan.xs} sm={colSpan.sm} md={colSpan.md} lg={colSpan.lg} xl={colSpan.xl} key={item.trainingId}>
        <Card
          hoverable
          className="lift-on-hover"
          bodyStyle={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column' }}
          style={{ height: '100%', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: isEscalated ? '1px solid #ff4d4f' : '1px solid #f0f0f0', opacity: isDeactivated ? 0.7 : 1 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '8px',
              backgroundColor: isEscalated ? '#fff1f0' : '#fff7e6',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {isEscalated ? <WarningOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} /> : <AppstoreOutlined style={{ fontSize: '20px', color: '#fa541c' }} />}
            </div>
            {isDeactivated && <Tag color="red" style={{ margin: 0, height: '22px', lineHeight: '20px', padding: '0 8px' }}>Closed</Tag>}
          </div>

          <Title level={5} ellipsis={{ rows: 2 }} style={{ fontSize: '16px', marginBottom: 6, flex: 1, minHeight: 44 }}>{item.trainingName}</Title>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: 6 }}>
            <Tag color={item.programType === 'TrainerLed' ? "orange" : "blue"} style={{ margin: 0, borderRadius: '4px' }}>
              {item.programType === 'TrainerLed' ? "Trainer Led" : "AI Led"}
            </Tag>
            <Text type="secondary" style={{ fontSize: '12px' }}>ID: {item.trainingId}</Text>
          </div>

          <div style={{ marginBottom: 10 }}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              {item.startDate ? `${dayjs(item.startDate.split('T')[0]).format('DD/MM/YYYY')} - ${dayjs(item.endDate.split('T')[0]).format('DD/MM/YYYY')}` : 'Evergreen (Available Anytime)'}
            </Text>
            {item.programType !== 'TrainerLed' && (
              <>
                {Number(item.estimatedEffortHours) > 0 && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                    <FieldTimeOutlined style={{ marginRight: 4 }} />
                    Assessment Duration: {item.estimatedEffortHours} hrs
                  </Text>
                )}
                {/* No cutoff to show when the program has none (0%) */}
                {Number(item.passingPercentage) > 0 && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                    <TrophyOutlined style={{ marginRight: 4 }} />
                    Passing Cutoff: {item.passingPercentage}%
                  </Text>
                )}
              </>
            )}
          </div>

          <div style={{ marginTop: 'auto' }}>
            {item.status !== TrainingStatus.NOT_ENROLLED && (
              <div style={{ marginBottom: 10 }}>
                {isEscalated ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                      <WarningOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
                      <Text type="danger" strong style={{ fontSize: '12px' }}>Escalated</Text>
                    </div>
                    {item.escalationDate && (
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        On: {dayjs(item.escalationDate.split('T')[0]).format('DD/MM/YYYY')}
                      </Text>
                    )}
                  </div>
                ) : isCompleted ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="success" strong style={{ fontSize: '12px' }}>Completed</Text>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>Progress</Text>
                      <Text strong style={{ fontSize: '12px' }}>{progressPercent}%</Text>
                    </div>
                    <Progress percent={progressPercent} showInfo={false} strokeColor={isInProgress ? "#fa541c" : "#d9d9d9"} size="small" />
                  </>
                )}
              </div>
            )}

            {isDeactivated ? (
              [TrainingStatus.NOT_ENROLLED, TrainingStatus.REQUESTED, TrainingStatus.DENIED].includes(item.status)
                ? <Button block size="large" icon={<StopOutlined />} disabled>Details (Closed)</Button>
                : <Button block size="large" icon={<StopOutlined />} style={{ background: '#8c8c8c', borderColor: '#8c8c8c', color: '#fff' }} onClick={() => handleActionClick(item)}>Details (Closed)</Button>
            ) : isCompleted ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button block size="large" icon={<SafetyCertificateOutlined />} onClick={() => handleActionClick(item)}>Successfully Passed</Button>
                <Button block icon={<InfoCircleOutlined />} onClick={() => setSelectedCalendarTraining(item)}>Show details</Button>
              </Space>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                {renderActionBtn(item)}
                <Button block icon={<InfoCircleOutlined />} onClick={() => setSelectedCalendarTraining(item)}>Show details</Button>
                {item.status !== TrainingStatus.NOT_ENROLLED && !item.isMandatory && (
                  <Popconfirm
                    title="Withdraw from training?"
                    description="Your current progress will be lost. Are you sure?"
                    onConfirm={() => handleWithdraw(item.trainingId)}
                    okText="Yes, Withdraw"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                  >
                    <Tooltip title="Withdraw from program">
                      <Button
                        block
                        type="text"
                        icon={<LogoutOutlined />}
                        style={{ marginTop: 8, fontSize: '13px', color: '#8c8c8c' }}
                      >
                        Withdraw
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            )}
          </div>
        </Card>
      </Col>
    );
  };

  const renderActionBtn = (item: Training) => {
    const isNotStarted = item.status === TrainingStatus.NOT_STARTED;
    const isRequested = item.status === TrainingStatus.REQUESTED;
    const isApproved = item.status === TrainingStatus.APPROVED;
    const isDenied = item.status === TrainingStatus.DENIED;
    const isEvergreen = item.trainingType === TrainingType.OPEN && (!item.startDate || item.startDate === '');
    
    const hasStarted = isEvergreen || (item.startDate !== '' && !dayjs().isBefore(dayjs(item.startDate.split('T')[0]).startOf('day')));
    const isRegistrationClosed = !isEvergreen && item.startDate !== '' && dayjs().isAfter(dayjs(item.startDate.split('T')[0]).endOf('day'));
    const hasEnded = !isEvergreen && item.endDate !== '' && dayjs().isAfter(dayjs(item.endDate.split('T')[0]).endOf('day'));

    // Handle Closed Programs (End Date passed) — enrolled participants can still view, others cannot
    const isEnrolled = item.status !== TrainingStatus.NOT_ENROLLED && item.status !== TrainingStatus.REQUESTED && item.status !== TrainingStatus.DENIED;
    if (hasEnded && !isEnrolled) {
      return (
        <Button
          block
          size="large"
          icon={<StopOutlined />}
          disabled
        >
          Program Closed
        </Button>
      );
    }

    if (isRequested) {
      return <Button block size="large" disabled icon={<ClockCircleOutlined />}>Approval Pending</Button>;
    }

    if (isDenied) {
      const canReRequest = !isRegistrationClosed;
      return (
        <Tooltip title={canReRequest ? "Your previous request was denied. You can try again before the program starts." : "Registration closed."}>
          <Button
            block
            size="large"
            type="primary"
            ghost
            danger={canReRequest}
            disabled={!canReRequest}
            onClick={() => canReRequest && handleRequestAccess(item)}
          >
            {canReRequest ? 'REQUEST AGAIN' : 'REGISTRATION CLOSED'}
          </Button>
        </Tooltip>
      );
    }

    // Accessible Programs (Either Enrolled/Started or Approved)
    const isAccessible = item.status === TrainingStatus.INCOMPLETE || 
                       item.status === TrainingStatus.PASSED || 
                       item.status === TrainingStatus.FAILED || 
                       item.status === TrainingStatus.IN_PROGRESS || 
                       isApproved || 
                       isNotStarted;

    if (isAccessible) {
      if (!hasStarted) {
        return (
          <Tooltip title={`Program will start on ${dayjs(item.startDate.split('T')[0]).format('MMM DD, YYYY')}`}>
            <Button
              block
              size="large"
              disabled
            >
              START (Scheduled)
            </Button>
          </Tooltip>
        );
      }
      return (
        <Button
          block
          size="large"
          type="primary"
          style={{ background: hasEnded ? '#8c8c8c' : (isNotStarted ? '#000' : '#fa541c'), borderColor: hasEnded ? '#8c8c8c' : (isNotStarted ? '#000' : '#fa541c') }}
          onClick={() => handleActionClick(item)}
        >
          {hasEnded ? 'CLOSED' : (isNotStarted ? 'START' : 'CONTINUE')}
        </Button>
      );
    }

    // Catalog Actions (Not yet interacted)
    if (item.status === TrainingStatus.NOT_ENROLLED && item.catalogVisible) {
      if (isRegistrationClosed) {
        return (
          <Tooltip title="Registration for this program closed once it started.">
            <Button block size="large" disabled icon={<StopOutlined />}>Registration Closed</Button>
          </Tooltip>
        );
      }

      if (item.trainingType === TrainingType.OPEN) {
        return (
          <Button
            block
            size="large"
            type="primary"
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
            onClick={() => handleEnroll(item)}
          >
            ENROLL & START
          </Button>
        );
      }
      if (item.trainingType === TrainingType.REQUESTABLE) {
        return (
          <Button
            block
            size="large"
            type="primary"
            ghost
            style={{ color: '#fa541c', borderColor: '#fa541c' }}
            onClick={() => handleRequestAccess(item)}
          >
            REQUEST ACCESS
          </Button>
        );
      }
    }

    // Show Locked status for unassigned Private programs in the Catalog
    if (item.status === TrainingStatus.NOT_ENROLLED && item.trainingType === TrainingType.PRIVATE) {
      return (
        <Button block size="large" disabled icon={<LockOutlined />}>
          PRIVATE
        </Button>
      );
    }

    return null;
  };

  const handleEnroll = async (training: Training) => {
    try {
      setLoading(true);
      await enrollInTraining(training.trainingId);
      message.success(`Successfully enrolled in ${training.trainingName}`);
      await loadTrainings();
      setActiveTab('my-learning');
    } catch (error) {
      message.error('Failed to enroll');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async (training: Training) => {
    try {
      setLoading(true);
      await requestTrainingAccess(training.trainingId);
      message.success(`Request sent for ${training.trainingName}`);
      await loadTrainings();
      setActiveTab('my-learning'); // Move to My Learning immediately
    } catch (error) {
      message.error('Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  // --- Filtered Data ---
  const filteredTrainings = useMemo(() => {
    switch (filterKey) {
      case 'notStarted': return myTrainings.filter(t => t.status === TrainingStatus.NOT_STARTED);
      case 'inProgress': return myTrainings.filter(t => t.status === TrainingStatus.IN_PROGRESS);
      case 'completed': return myTrainings.filter(t => t.status === TrainingStatus.COMPLETED);
      case 'incomplete': return myTrainings.filter(t => t.status === TrainingStatus.INCOMPLETE || t.status === TrainingStatus.ESCALATED);
      default: return myTrainings;
    }
  }, [myTrainings, filterKey]);

  const handleActionClick = (training: Training) => {
    navigate(`/training/${encodeURIComponent(training.trainingId)}`);
  };

  // --- Sub-components ---
  const StatBadge = ({ icon, label, count, active, onClick, color }: any) => (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        padding: '8px 16px',
        borderRadius: '20px',
        backgroundColor: active ? (color === 'orange' ? '#fff7e6' : '#f5f5f5') : 'transparent',
        color: active && color === 'orange' ? '#fa541c' : 'inherit',
        transition: 'all 0.3s'
      }}
    >
      <span style={{ marginRight: 8, color: active && color === 'orange' ? '#fa541c' : '#8c8c8c' }}>{icon}</span>
      <Text style={{ marginRight: 8, fontWeight: active ? 600 : 400, color: 'inherit' }}>{label}</Text>
      <span style={{ fontWeight: 'bold' }}>{count}</span>
    </div>
  );

  if (loading) {
    return (
      <Layout style={{ minHeight: '100%', background: '#fff' }}>
        <div style={{ padding: '16px 40px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
          <Row align="middle" justify="space-between">
            <Col style={{ width: 240 }}>
              <Skeleton active title={{ width: '100%' }} paragraph={false} />
            </Col>
            <Col>
              <Space size="large">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton.Button key={i} active size="small" style={{ width: 96 }} />
                ))}
              </Space>
            </Col>
          </Row>
        </div>

        <Layout style={{ minHeight: 0 }}>
          <Sider width={200} theme="light" style={{ borderRight: '1px solid #f0f0f0' }}>
            <div style={{ padding: '24px 16px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton.Button key={i} active block size="small" />
                ))}
              </Space>
            </div>
          </Sider>

          <Content style={{ padding: '24px 40px', background: '#fcfcfc' }}>
            <Skeleton active title={false} paragraph={{ rows: 1, width: '60%' }} style={{ marginBottom: 16 }} />
            <Space size="middle" style={{ marginBottom: 24 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton.Button key={i} active size="default" style={{ width: 120 }} />
              ))}
            </Space>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Row gutter={[16, 16]}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Col xs={24} sm={12} key={i}>
                      <Card>
                        <Skeleton active avatar paragraph={{ rows: 2 }} />
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>
              <Col xs={24} md={12}>
                <Card>
                  <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 5 }} />
                </Card>
              </Col>
            </Row>
          </Content>
        </Layout>
      </Layout>
    );
  }

  // --- Render List Item ---
  // `compact` renders a fully-stacked single-column card instead of the fixed-width
  // 3-column row - needed in My Learning now that its list sits in a ~50%-width
  // panel, where the old fixed pixel columns (60px + auto + 400px) no longer fit
  // and squeezed/misaligned the action buttons. Catalog keeps the original layout.
  const renderTrainingItem = (item: Training, compact: boolean = false) => {
    const isCompleted = item.status === TrainingStatus.COMPLETED;
    const isEscalated = item.status === TrainingStatus.ESCALATED;
    const isInProgress = item.status === TrainingStatus.IN_PROGRESS;
    const isNotStarted = item.status === TrainingStatus.NOT_STARTED;
    const isEvergreen = !item.endDate || item.startDate === '';
    const isDeactivated = !item.active;

    // Use actual percentage from API
    const progressPercent = item.percentage !== undefined ? item.percentage : (isCompleted ? 100 : 0);
    if (isEscalated) console.log('Escalated Item:', item);

    const iconBox = (
      <div style={{
        width: compact ? 48 : 60,
        height: compact ? 48 : 60,
        borderRadius: '12px',
        backgroundColor: isEscalated ? '#fff1f0' : '#fff7e6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {isEscalated ? <WarningOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} /> : <AppstoreOutlined style={{ fontSize: '24px', color: '#fa541c' }} />}
      </div>
    );

    const infoNode = (
      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        <Space size={8} wrap>
          <Tag color={item.programType === 'TrainerLed' ? "orange" : "blue"} style={{ margin: 0, borderRadius: '4px' }}>
            {item.programType === 'TrainerLed' ? "Trainer Led" : "AI Led"}
          </Tag>
          <Text type="secondary" style={{ fontSize: '12px' }}>ID: {item.trainingId}</Text>
          {isEscalated && <Tag color="error">ESCALATED</Tag>}
          {isDeactivated && <Tag color="red">Closed</Tag>}
        </Space>
        <Title level={5} style={{ margin: 0, fontSize: '18px' }}>{item.trainingName}</Title>
        <Space size={16} wrap style={{ marginTop: 4 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {isEvergreen ? 'Evergreen (Available Anytime)' : (
              `${dayjs(item.startDate.split('T')[0]).format('DD/MM/YYYY')} - ${dayjs(item.endDate.split('T')[0]).format('DD/MM/YYYY')}`
            )}
          </Text>
          {item.programType !== 'TrainerLed' && (
            <>
              {Number(item.estimatedEffortHours) > 0 && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <FieldTimeOutlined style={{ marginRight: 4 }} />
                  Assessment Duration: {item.estimatedEffortHours} hrs
                </Text>
              )}
              {Number(item.passingPercentage) > 0 && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <TrophyOutlined style={{ marginRight: 4 }} />
                  Passing Cutoff: {item.passingPercentage}%
                </Text>
              )}
            </>
          )}
        </Space>
      </Space>
    );

    const statusNode = item.status !== TrainingStatus.NOT_ENROLLED && (
      isEscalated ? (
        <div style={{ textAlign: compact ? 'left' : 'right' }}>
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <Text type="danger" strong style={{ fontSize: '12px' }}>Action Required</Text>
          </Space>
          {item.escalationDate && (
            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
              Escalated on: {dayjs(item.escalationDate.split('T')[0]).format('DD/MM/YYYY')}
            </div>
          )}
        </div>
      ) : isCompleted ? (
        <div style={{ textAlign: compact ? 'left' : 'center' }}>
          <Text type="success" strong style={{ fontSize: '12px' }}>Completed</Text>
          <Progress percent={100} showInfo={false} strokeColor="#52c41a" size="small" />
        </div>
      ) : isNotStarted ? (
        <div style={{ textAlign: compact ? 'left' : 'right' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>Not Started</Text>
          <Progress percent={0} showInfo={false} strokeColor="#d9d9d9" size="small" />
        </div>
      ) : (
        <div style={{ textAlign: compact ? 'left' : 'right' }}>
          <Space>
            <Text type="warning" strong style={{ fontSize: '12px' }}>{progressPercent}% Progress</Text>
          </Space>
          <Progress percent={progressPercent} showInfo={false} strokeColor="#fa541c" size="small" />
        </div>
      )
    );

    const actionsNode = isDeactivated ? (
      [TrainingStatus.NOT_ENROLLED, TrainingStatus.REQUESTED, TrainingStatus.DENIED].includes(item.status)
        ? <Button block size="large" icon={<StopOutlined />} disabled>Details (Closed)</Button>
        : <Button block size="large" icon={<StopOutlined />} style={{ background: '#8c8c8c', borderColor: '#8c8c8c', color: '#fff' }} onClick={() => handleActionClick(item)}>Details (Closed)</Button>
    ) : isCompleted ? (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button
          icon={<SafetyCertificateOutlined />}
          block
          size="large"
          onClick={() => handleActionClick(item)}
        >
          Successfully Passed
        </Button>
        <Button block icon={<InfoCircleOutlined />} onClick={() => setSelectedCalendarTraining(item)}>Show details</Button>
      </Space>
    ) : (
      <Space direction="vertical" style={{ width: '100%' }}>
        {renderActionBtn(item)}
        <Button block icon={<InfoCircleOutlined />} onClick={() => setSelectedCalendarTraining(item)}>Show details</Button>
        {item.status !== TrainingStatus.NOT_ENROLLED && !item.isMandatory && (
          <Popconfirm
            title="Withdraw from training?"
            description="Your current progress will be lost. Are you sure?"
            onConfirm={() => handleWithdraw(item.trainingId)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Withdraw from program">
              <Button
                type="text"
                icon={<LogoutOutlined />}
                size="small"
                style={{ color: '#8c8c8c' }}
              />
            </Tooltip>
          </Popconfirm>
        )}
      </Space>
    );

    const cardStyle = {
      marginBottom: 16,
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      border: isEscalated ? '1px solid #ff4d4f' : '1px solid #f0f0f0',
      opacity: isDeactivated ? 0.7 : 1
    };

    if (compact) {
      return (
        <Card
          key={item.trainingId}
          className="lift-on-hover"
          bodyStyle={{ padding: '20px' }}
          style={cardStyle}
          hoverable
        >
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            {iconBox}
            <div style={{ flex: 1, minWidth: 0 }}>{infoNode}</div>
          </div>
          {statusNode && <div style={{ marginBottom: 12 }}>{statusNode}</div>}
          {actionsNode}
        </Card>
      );
    }

    return (
      <Card
        key={item.trainingId}
        className="lift-on-hover"
        bodyStyle={{ padding: '24px' }}
        style={cardStyle}
        hoverable
      >
        <Row align="middle" gutter={[24, 16]}>
          <Col flex="60px">{iconBox}</Col>

          <Col flex="auto">{infoNode}</Col>

          <Col flex="400px">
            <Row align="middle" justify="end" gutter={24}>
              <Col span={12}>{statusNode}</Col>
              <Col span={12} style={{ textAlign: 'right' }}>{actionsNode}</Col>
            </Row>
          </Col>
        </Row>
      </Card>
    );
  };

  // My Learning's right-hand panel: a flat, date-grouped agenda list (no grid, no
  // click-through) - see scheduleDateBlocks above for the filtering/date-range logic.
  const renderSchedulePanel = () => (
    <Card
      style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}
      bodyStyle={{ padding: '16px' }}
    >
      <Flex align="center" gap={8} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
        <CalendarOutlined style={{ color: '#1890ff', fontSize: 16 }} />
        <Title level={5} style={{ margin: 0 }}>Upcoming Schedule</Title>
      </Flex>

      <Flex vertical gap={4} style={{ maxHeight: 640, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
          {scheduleDateBlocks.map(({ date, programs }, blockIndex) => (
            <Flex
              gap={16}
              className="ld-schedule-date-block"
              key={date.format('YYYY-MM-DD')}
              style={{ padding: '12px 8px', borderRadius: 8, borderTop: blockIndex > 0 ? '1px solid #f5f5f5' : undefined }}
            >
              <div style={{ flex: '0 0 48px', textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 600, color: 'rgba(0, 0, 0, 0.85)', lineHeight: 1.2 }}>{date.format('DD')}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c', textTransform: 'uppercase' }}>{date.format('MMM')}</div>
              </div>
              <Flex vertical gap={8} style={{ flex: 1, minWidth: 0, paddingLeft: 16, borderLeft: '1px solid #f0f0f0' }}>
                {programs.map(t => {
                  const tIndex = trainings.findIndex(item => item.trainingId === t.trainingId);
                  const palette = getTrainingPalette(tIndex);
                  return (
                    <Flex
                      align="center"
                      gap={10}
                      key={t.trainingId}
                      style={{ background: palette.bg, borderLeft: `3px solid ${palette.main}`, padding: '6px 10px', borderRadius: 6 }}
                    >
                      <Flex align="center" justify="center" style={{ width: 22, height: 22, borderRadius: 6, background: palette.bg, color: palette.main, fontSize: 12, flexShrink: 0 }}>
                        <AppstoreOutlined />
                      </Flex>
                      <Text style={{ color: palette.main, fontSize: 14, fontWeight: 500, flex: 1, minWidth: 0 }} ellipsis={{ tooltip: t.trainingName }}>
                        {t.trainingName}
                      </Text>
                      <Flex align="center" gap={6} style={{ flexShrink: 0 }}>
                        {t.trainingType === TrainingType.PRIVATE && (
                          <Tag color="blue" style={{ margin: 0, fontSize: 12, lineHeight: '18px', padding: '0 6px' }}>Private</Tag>
                        )}
                        {t.trainingType === TrainingType.REQUESTABLE && (
                          <Tag color="orange" style={{ margin: 0, fontSize: 12, lineHeight: '18px', padding: '0 6px' }}>Requestable</Tag>
                        )}
                        <Tooltip title="Show details">
                          <Button
                            type="text"
                            size="small"
                            icon={<InfoCircleOutlined />}
                            data-testid={`schedule-info-${t.trainingId}`}
                            onClick={() => setSelectedCalendarTraining(t)}
                            style={{ width: 22, height: 22, minWidth: 22, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}
                          />
                        </Tooltip>
                      </Flex>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
          ))}
      </Flex>
    </Card>
  );

  return (
    <Layout style={{ minHeight: '100%', background: '#fff' }}>
      {/* Top Bar with User & Overall Stats */}
      <div style={{ padding: '16px 40px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={4} style={{ margin: 0 }}>Hello, <span style={{ fontWeight: 400 }}>{user?.name || 'Learner'}</span></Title>
          </Col>
          <Col>
            <Space size="large">
              {/* Summary Stats Row */}
              <StatBadge
                icon={<AppstoreOutlined style={{ color: '#fa541c' }} />}
                label="All Programs"
                count={stats.all}
                onClick={() => { }}
              />
              <StatBadge icon={<ClockCircleOutlined />} label="Not Started" count={stats.notStarted} />
              <StatBadge icon={<PlayCircleOutlined style={{ color: '#1890ff' }} />} label="In Progress" count={stats.inProgress} />
              <StatBadge icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />} label="Completed" count={stats.completed} />
              <StatBadge icon={<WarningOutlined style={{ color: '#ff4d4f' }} />} label="Incomplete" count={stats.incomplete} />
            </Space>
          </Col>
        </Row>
      </div>

      <Layout style={{ minHeight: 0 }}>
        {/* Visual Sidebar for Filters */}
        <Sider width={200} theme="light" style={{ borderRight: '1px solid #f0f0f0' }}>
          <div style={{ padding: '24px 0' }}>
            <Menu
              mode="inline"
              selectedKeys={[filterKey]}
              onClick={({ key }) => setFilterKey(key)}
              style={{ borderRight: 0 }}
              items={[
                { key: 'all', icon: <AppstoreOutlined />, label: 'All Programs' },
                { key: 'notStarted', icon: <ClockCircleOutlined />, label: 'Not Started' },
                { key: 'inProgress', icon: <PlayCircleOutlined />, label: 'In Progress' },
                { key: 'completed', icon: <CheckCircleOutlined />, label: 'Completed' },
                { key: 'incomplete', icon: <WarningOutlined />, label: 'Incomplete' },
              ].map(item => ({
                ...item,
                style: filterKey === item.key ? { color: '#fa541c', backgroundColor: '#fff7e6', borderRight: '3px solid #fa541c' } : {}
              }))}
            />
          </div>
        </Sider>

        {/* Main Content */}
        <Content style={{ padding: '24px 40px', background: '#fcfcfc' }}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              <InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              Efficiently manage your learning pathways and professional development goals.
            </Text>
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'my-learning',
                label: <span><BookOutlined /> My Learning</span>,
              },
              {
                key: 'catalog',
                label: <span><AppstoreOutlined /> Training Catalog</span>,
              },
              {
                key: 'calendar',
                label: <span><CalendarOutlined /> Training Calendar</span>,
              }
            ]}
          />

          <div style={{ marginTop: 24 }}>
            {activeTab === 'my-learning' && (
              <Row gutter={24}>
                <Col xs={24} md={hasUpcomingSchedule ? 16 : 24} className="ld-courses-panel">
                  <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>Enrolled Courses</Title>
                    <Space>
                      <Button.Group>
                        <Button
                          icon={<UnorderedListOutlined />}
                          type={viewMode === 'list' ? 'primary' : 'default'}
                          onClick={() => setViewMode('list')}
                        />
                        <Button
                          icon={<AppstoreOutlined />}
                          type={viewMode === 'grid' ? 'primary' : 'default'}
                          onClick={() => setViewMode('grid')}
                        />
                      </Button.Group>
                    </Space>
                  </div>

                  {filteredTrainings.length === 0 ? (
                    <Empty description="No courses found in this category" style={{ marginTop: 60 }} />
                  ) : (
                    <div style={{ maxHeight: 640, overflowY: 'auto', overflowX: 'hidden', padding: '8px 16px 16px 4px' }}>
                      {viewMode === 'list' ? (
                        filteredTrainings.map(t => renderTrainingItem(t, true))
                      ) : (
                        <Row gutter={[24, 24]}>
                          {filteredTrainings.map(t => renderTrainingGridItem(t, { xs: 24, sm: 12, md: 12, lg: 8, xl: 8 }))}
                        </Row>
                      )}
                    </div>
                  )}
                </Col>

                {hasUpcomingSchedule && (
                  <Col xs={24} md={8} className="ld-schedule-col" style={{ position: screens.md ? 'sticky' : 'static', top: 24, alignSelf: 'flex-start' }}>
                    {renderSchedulePanel()}
                  </Col>
                )}
              </Row>
            )}

            {activeTab === 'catalog' && (
              <>
                <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Title level={4} style={{ margin: 0 }}>Available for You</Title>
                  <Space>
                    <Button.Group>
                      <Button
                        icon={<UnorderedListOutlined />}
                        type={viewMode === 'list' ? 'primary' : 'default'}
                        onClick={() => setViewMode('list')}
                      />
                      <Button
                        icon={<AppstoreOutlined />}
                        type={viewMode === 'grid' ? 'primary' : 'default'}
                        onClick={() => setViewMode('grid')}
                      />
                    </Button.Group>
                  </Space>
                </div>

                {catalogTrainings.length === 0 ? (
                  <Empty description="No other courses available right now" style={{ marginTop: 60 }} />
                ) : (
                  <div>
                    {viewMode === 'list' ? (
                      catalogTrainings.map(t => renderTrainingItem(t))
                    ) : (
                      <Row gutter={[24, 24]}>
                        {catalogTrainings.map(t => renderTrainingGridItem(t))}
                      </Row>
                    )}
                  </div>
                )}
              </>
            )}

            {activeTab === 'calendar' && (
              <>
                <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Title level={4} style={{ margin: 0 }}>Training Explorer & Calendar</Title>
                  <Space>
                    <Text type="secondary">Filter Programs:</Text>
                    <Select
                      mode="multiple"
                      placeholder="Select programs to view"
                      style={{ minWidth: '300px' }}
                      value={selectedCalendarPrograms}
                      onChange={setSelectedCalendarPrograms}
                      allowClear
                      maxTagCount="responsive"
                    >
                      {trainings
                        .filter(t => t.startDate && t.endDate)
                        .map(t => (
                          <Select.Option key={t.trainingId} value={t.trainingId}>
                            {t.trainingName}
                          </Select.Option>
                        ))}
                    </Select>
                  </Space>
                </div>

                <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '12px' }}>
                  <Calendar
                    fullscreen={true}
                    dateCellRender={(date) => {
                      const activeTrainings = trainings.filter(t => {
                        // Filter out Evergreen programs (no dates)
                        if (!t.startDate || !t.endDate) return false;

                        // Filter by selected programs if any
                        if (selectedCalendarPrograms.length > 0 && !selectedCalendarPrograms.includes(t.trainingId)) {
                          return false;
                        }
                        const start = dayjs(t.startDate.split('T')[0]);
                        const end = dayjs(t.endDate.split('T')[0]);
                        return !date.isBefore(start, 'day') && !date.isAfter(end, 'day');
                      });

                      if (activeTrainings.length === 0) return null;

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '2px' }}>
                          {activeTrainings.map(t => {
                            const isStart = date.isSame(dayjs(t.startDate.split('T')[0]), 'day');
                            const isEnd = date.isSame(dayjs(t.endDate.split('T')[0]), 'day');
                            const tIndex = trainings.findIndex(item => item.trainingId === t.trainingId);
                            const palette = getTrainingPalette(tIndex);

                            return (
                              <div
                                key={t.trainingId}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCalendarTraining(t);
                                }}
                                style={{
                                  cursor: 'pointer',
                                  background: palette.bg,
                                  borderLeft: `3px solid ${palette.main}`,
                                  borderRight: isEnd ? `3px solid ${palette.main}` : 'none',
                                  padding: '1px 4px',
                                  borderRadius: isStart ? '4px 0 0 4px' : (isEnd ? '0 4px 4px 0' : '0'),
                                  fontSize: '11px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  color: palette.main,
                                  fontWeight: isStart ? 'bold' : 'normal',
                                  lineHeight: '1.4'
                                }}
                              >
                                {isStart ? t.trainingName : '•'}
                              </div>
                            );
                          })}
                        </div>
                      );
                    }}
                  />
                </Card>
              </>
            )}

          </div>
        </Content>
      </Layout>

      <Modal
        title={
          <Space>
            <BookOutlined style={{ color: '#1890ff' }} />
            {selectedCalendarTraining?.trainingName}
          </Space>
        }
        width={900}
        centered
        open={!!selectedCalendarTraining}
        onCancel={() => setSelectedCalendarTraining(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedCalendarTraining(null)}>
            Close
          </Button>,
          (() => {
            if (!selectedCalendarTraining) return null;
            const isEvergreen = !selectedCalendarTraining.startDate;
            const hasStarted = selectedCalendarTraining.startDate && dayjs().isAfter(dayjs(selectedCalendarTraining.startDate.split('T')[0]).endOf('day'));
            const registrationClosed = !isEvergreen && hasStarted;

            if (selectedCalendarTraining.trainingType === TrainingType.REQUESTABLE &&
              selectedCalendarTraining.status === TrainingStatus.NOT_ENROLLED &&
              !registrationClosed) {
              return (
                <Button
                  type="primary"
                  ghost
                  style={{ color: '#fa541c', borderColor: '#fa541c' }}
                  onClick={() => {
                    setSelectedCalendarTraining(null);
                    setActiveTab('catalog');
                  }}
                >
                  Go to Catalog to Request
                </Button>
              );
            }
            return null;
          })()
        ].filter(Boolean)}
      >
        {selectedCalendarTraining && (
          <div style={{ padding: '8px 0' }}>
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Program Overview</Text>
              <Card size="small" style={{ backgroundColor: '#fafafa', borderRadius: '8px' }}>
                <Row gutter={[16, 12]}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Training ID</Text>
                    <div style={{ fontWeight: 500 }}>{selectedCalendarTraining.trainingId}</div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Type</Text>
                    <div><Tag color={selectedCalendarTraining.trainingType === 'Open' ? 'green' : (selectedCalendarTraining.trainingType === 'Requestable' ? 'orange' : 'blue')}>{selectedCalendarTraining.trainingType || 'Private'}</Tag></div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Duration Schedule</Text>
                    <div style={{ fontSize: '13px' }}>
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      {selectedCalendarTraining.startDate ? `${dayjs(selectedCalendarTraining.startDate.split('T')[0]).format('MMM DD, YYYY')} - ${dayjs(selectedCalendarTraining.endDate.split('T')[0]).format('MMM DD, YYYY')}` : 'Evergreen (Available Anytime)'}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {Number(selectedCalendarTraining.passingPercentage) > 0 ? 'Assessment Duration & Passing Cutoff' : 'Assessment Duration'}
                    </Text>
                    <div style={{ fontSize: '13px' }}>
                      <FieldTimeOutlined style={{ marginRight: 4 }} /> {selectedCalendarTraining.estimatedEffortHours || 0} hrs 
                      {Number(selectedCalendarTraining.passingPercentage) > 0 && (
                        <>
                          <span style={{ margin: '0 8px', color: '#d9d9d9' }}>|</span>
                          <TrophyOutlined style={{ marginRight: 4 }} /> {selectedCalendarTraining.passingPercentage}%
                        </>
                      )}
                    </div>
                  </Col>
                  <Col span={24}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Current Status</Text>
                    <div style={{ marginTop: 4 }}>
                      {(() => {
                        const s = selectedCalendarTraining.status;
                        if (s === TrainingStatus.NOT_ENROLLED) return <Badge status="default" text="Not Enrolled" />;
                        if (s === TrainingStatus.REQUESTED) return <Badge status="warning" text="Pending Approval" />;
                        if (s === TrainingStatus.COMPLETED) return <Badge status="success" text="Successfully Completed" />;
                        if (s === TrainingStatus.IN_PROGRESS || s === TrainingStatus.NOT_STARTED) return <Badge status="processing" text="Enrolled" />;
                        if (s === TrainingStatus.ESCALATED || s === TrainingStatus.INCOMPLETE) return <Badge status="error" text="Incomplete / Escalated" />;
                        return <Badge status="default" text={s} />;
                      })()}
                    </div>
                  </Col>
                </Row>
              </Card>
            </div>

            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>Modules & Curriculum</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>{displayModules.length} Modules</Text>
              </div>

              {modulesLoading ? (
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
                  <Spin size="small" tip="Loading curriculum..." />
                </div>
              ) : displayModules.length > 0 ? (
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
                    {displayModules.map((module, index) => {
                      const { name: moduleDisplayName, description: moduleDescription } = parseModuleId(module.name || "");

                      return (
                        <div 
                          key={module.moduleId} 
                          style={{ 
                            padding: '12px 16px', 
                            borderBottom: index === displayModules.length - 1 ? 'none' : '1px solid #f0f0f0',
                            display: 'flex',
                            alignItems: 'flex-start',
                            backgroundColor: '#fff'
                          }}
                        >
                          <div style={{ 
                            minWidth: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            backgroundColor: '#f5f5f5', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontSize: '11px', 
                            fontWeight: 'bold', 
                            color: '#8c8c8c',
                            marginRight: 12,
                            marginTop: 2
                          }}>
                            {index + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <Text strong style={{ fontSize: '14px', display: 'block', lineHeight: 1.4 }}>{moduleDisplayName}</Text>
                            {selectedCalendarTraining?.programType !== 'TrainerLed' && (() => {
                              const rawDesc = module.description || moduleDescription;
                              if (!rawDesc) return null;
                              // Strip HTML tags for clean display
                              const cleanDesc = rawDesc.replace(/<[^>]*>?/gm, '').trim();
                              if (!cleanDesc) return null;
                              
                              return (
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4, lineHeight: 1.5 }}>
                                  {cleanDesc}
                                </Text>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No module details available" />
              )}
            </div>
          </div>
        )}
      </Modal>

    </Layout>
  );
};

export default UserDashboard;